//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { useEffect, useState } from 'react'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Switch,
  Tab,
  Typography,
  useTheme,
} from '@mui/material'
import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'
import { ReportCategory, ReportDataShape, RoleEnum } from '@shared/Enums'
import { EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'
import { ethers, JsonRpcProvider, ZeroAddress } from 'ethers'
import {
  ADMIN_REPORT_DESCRIPTORS,
  LENDER_REPORT_DESCRIPTORS,
  REPORT_DESCRIPTOR_UNSELECTED,
  REPORT_FETCH_DESCRIPTOR_POOL_EVENTS,
  REPORT_TICKET_PENDING,
  ReportDescriptor,
  ReportSelectorType,
} from './ReportDescriptors'

import dayjs, { Dayjs } from 'dayjs'

import {
  fetchAllPoolAccountAccessPairs,
  fetchPoolAccountAccessPairs,
  fetchPoolsAllChains,
} from '@react/fetchers/PoolFetchers'
import { poolActions } from '@react/redux/poolSlice'
import { logOT } from '@shared/loggingShared'

import { reportDefinitionPut, reportDefinitionsForShape } from '@react/fetchers/reportDefinitionFetchers'
import { ReportDefinitionCTO } from '@shared/ReportDefinitionCTO'
import { TimeStep } from '@shared/Enums'
import { PoolContractCTO } from '@shared/sharedTypes'
import ReportDateRangeSelector from './ReportDateRangePicker'
import { ReportExplorer } from './ReportExplorer'
import ReportLenderSelector from './ReportLenderSelector'
import { ReportRowGeneratorClass } from './ReportRowGeneratorClass'
import ReportVaultMultiSelect from './ReportVaultMultiSelect'
import { otAssert } from '@shared/conversions'
import ReportDateTimePicker from './ReportDateTimePicker'
import ReportDateStepPicker from './ReportDateStepPicker'

export default function ReportingPage(): React.ReactElement {
  const {
    chainConfiguration: { chainTimestampDelta, providerReadOnly },
    stageConfiguration: { serverApiBaseUrl, restAPIKey, isLyt, stageName },
    userConnection: { roleEnum, accountAddr },
  } = useOtChainState()

  const [, forceRerender] = React.useState({})
  const theme = useTheme()

  const [isConsolidatedReport, setIsConsolidatedReport] = useState(false)
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().utc().subtract(7, 'day').add(chainTimestampDelta, 'seconds').startOf('day'),
    dayjs().utc().subtract(1, 'day').add(chainTimestampDelta, 'seconds').startOf('day'),
  ])
  const [endDateTime, setEndDateTime] = useState<Dayjs>(dayjs().utc().add(chainTimestampDelta, 'seconds'))
  const [timeStep, setTimeStep] = useState<TimeStep>(TimeStep.Day)
  const [keyIndex, setKeyIndex] = useState(0)
  const [fetching, setFetching] = useState(false)
  const [reportDefinition, setReportDefinition] = useState<ReportDefinitionCTO>({
    reportName: ReportCategory.UNSELECTED,
    reportUuid: ethers.uuidV4(ethers.randomBytes(16)),
    reportDescriptorKey: REPORT_DESCRIPTOR_UNSELECTED.key,
    timestamp: Date.now(),
    dataShape: ReportDataShape.UNKNOWN,
    accountAddr: ZeroAddress,
  })

  const [vaultCTOs, setVaultCTOs] = useState<Record<EthAddr, PoolContractCTO>>({})

  const [selectedPoolAddrs, setSelectedPoolAddrs] = useState<EthAddr[]>([])
  const [reportRowGeneratorClasses, setReportRowGeneratorClasses] = useState<ReportRowGeneratorClass[]>([])
  const [reportRowGeneratorClassForAllPools, setReportRowGeneratorClassForAllPools] =
    useState<ReportRowGeneratorClass>()

  const { accountPoolPairs } = useOtSelector((state) => state.poolState)

  const [selectedTab, setSelectedTab] = React.useState<EthAddr>(ZeroAddress)
  const [lenderAddr, setLenderAddr] = useState<EthAddr>(
    roleEnum === RoleEnum.LENDER && accountAddr ? accountAddr : ZeroAddress
  )
  const baseDescriptors =
    roleEnum === RoleEnum.POOL_ADMIN || roleEnum === RoleEnum.BORROWER
      ? ADMIN_REPORT_DESCRIPTORS
      : LENDER_REPORT_DESCRIPTORS

  const getLytReportDescriptors = (descriptors: ReportDescriptor[]): ReportDescriptor[] => {
    return descriptors.filter((descriptor) => {
      return (
        descriptor.key === 'RESERVES' ||
        descriptor.key === 'POOL_EVENTS' ||
        descriptor.dataShape.includes('LYT') ||
        descriptor.dataShape === 'SIERRA_EVENTS' ||
        descriptor.dataShape === 'SIERRA_RESERVES'
      )
    })
  }
  const ReportDescriptors = isLyt ? getLytReportDescriptors(baseDescriptors) : baseDescriptors

  const [reportDescriptor, setReportDescriptor] = useState<ReportDescriptor>(
    ReportDescriptors.find(
      (reportDescriptor) => reportDescriptor.key === localStorage.getItem('reportDescriptorKey')
    ) ?? REPORT_DESCRIPTOR_UNSELECTED
  )
  const dispatch = useOtDispatch()

  useEffect(() => {
    if (reportDescriptor) {
      fetchReportDefinition(reportDescriptor)
        .then((_reportDefinition) => {
          setReportDefinition(_reportDefinition)
        })
        .catch((error) => {
          logOT.error('Error fetching report definition', error)
        })
    }
  }, [reportDescriptor])
  useEffect(() => {
    regenerateReportRowGeneratorClasses(selectedPoolAddrs, reportDescriptor)
    setKeyIndex(keyIndex + 1)
  }, [reportDefinition])
  useEffect(() => {
    const fetchPoolsAllChainsAsyncFunction = async () => {
      if (roleEnum === RoleEnum.LENDER && accountAddr) {
        const pairs: PoolAccountAccessPair[] = await fetchPoolAccountAccessPairs(
          serverApiBaseUrl,
          restAPIKey,
          accountAddr
        )
        dispatch(poolActions.addPoolAccountAccessPairs({ poolAccountAccessPairs: pairs }))
        const pools = await fetchPoolsAllChains(serverApiBaseUrl, restAPIKey)
        const filteredPools = pools.filter((pool) => pairs.some((pair) => pair.poolAddr === pool.poolAddr))

        const _vaultCTOs = filteredPools.reduce(
          (acc, pool) => {
            acc[pool.poolAddr] = pool
            return acc
          },
          {} as Record<EthAddr, PoolContractCTO>
        )

        setVaultCTOs(_vaultCTOs)
      } else {
        const pairs: PoolAccountAccessPair[] = await fetchAllPoolAccountAccessPairs(serverApiBaseUrl, restAPIKey)
        dispatch(poolActions.addPoolAccountAccessPairs({ poolAccountAccessPairs: pairs }))
        const pools = await fetchPoolsAllChains(serverApiBaseUrl, restAPIKey)
        const _vaultCTOs = pools.reduce(
          (acc, pool) => {
            acc[pool.poolAddr] = pool
            return acc
          },
          {} as Record<EthAddr, PoolContractCTO>
        )

        setVaultCTOs(_vaultCTOs)
      }
    }
    fetchPoolsAllChainsAsyncFunction().catch((error) => {
      logOT.error('Error fetching pools all chains', error)
    })
  }, [roleEnum])

  const regenerateReportRowGeneratorClasses = (_selectedPoolAddrs: EthAddr[], _reportDescriptor: ReportDescriptor) => {
    const nextReportRowGeneratorClasses: ReportRowGeneratorClass[] = []
    const reportDescriptorKey = _reportDescriptor.key

    _selectedPoolAddrs.forEach((poolAddr) => {
      const _current = reportRowGeneratorClasses.find(
        (_reportRowGeneratorClass) => _reportRowGeneratorClass.poolAddr === poolAddr
      )
      if (_current && _current.reportDescriptor.key === reportDescriptorKey) {
        nextReportRowGeneratorClasses.push(_current)
      } else {
        nextReportRowGeneratorClasses.push(
          new ReportRowGeneratorClass({
            poolAddr,
            reportDescriptor: _reportDescriptor,
            dateRange,
            providerReadOnly: providerReadOnly as JsonRpcProvider,
            serverApiBaseUrl,
            restAPIKey,
            lenderAddr,
            stageName,
            endDateTime,
            timeStep,
          })
        )
      }
    })

    setReportRowGeneratorClasses(nextReportRowGeneratorClasses)

    const _reportRowGeneratorClassForAllPools = new ReportRowGeneratorClass({
      poolAddr: ZeroAddress,
      reportDescriptor: _reportDescriptor,
      dateRange,
      providerReadOnly: providerReadOnly as JsonRpcProvider,
      serverApiBaseUrl,
      restAPIKey,
      lenderAddr,
      stageName,
      endDateTime,
      timeStep,
    })
    if (_reportDescriptor.key === REPORT_TICKET_PENDING.key) {
      setIsConsolidatedReport(true)
    } else {
      setIsConsolidatedReport(false)
    }

    setReportRowGeneratorClassForAllPools(_reportRowGeneratorClassForAllPools)
  }

  const onSelectedPoolAddrsChange = (_selectedPoolAddrs: EthAddr[]) => {
    setSelectedPoolAddrs(_selectedPoolAddrs)
    if (!_selectedPoolAddrs.includes(selectedTab) && _selectedPoolAddrs.length > 0) {
      setSelectedTab(_selectedPoolAddrs[0])
    }
    regenerateReportRowGeneratorClasses(_selectedPoolAddrs, reportDescriptor)
  }

  let _poolAddrs: EthAddr[] = []
  if (roleEnum === RoleEnum.LENDER && accountAddr && accountPoolPairs) {
    _poolAddrs = Object.keys(accountPoolPairs[accountAddr] ?? {}).filter((_poolAddr) => vaultCTOs[_poolAddr])
  } else if (vaultCTOs) {
    _poolAddrs = Object.values(vaultCTOs ?? []).map((_poolOverviewCTO) => _poolOverviewCTO.poolAddr)
  }
  const poolAddrs = _poolAddrs

  let minDate = dayjs().utc().subtract(1000, 'day').add(chainTimestampDelta, 'seconds').startOf('day')
  selectedPoolAddrs.forEach((poolAddr) => {
    if (vaultCTOs[poolAddr]?.creationTimestamp) {
      const minDate2 = dayjs((vaultCTOs[poolAddr]?.creationTimestamp ?? 0) * 1000)
        .utc()
        .add(1, 'day')
        .startOf('day')
      if (minDate2 > minDate) {
        minDate = minDate2
      }
    }
  })

  const lenderAddrSet = new Set<EthAddr>()

  Object.keys(accountPoolPairs ?? {}).forEach((lenderAddr) => {
    const poolAddrs = Object.keys(accountPoolPairs[lenderAddr])

    if (poolAddrs.some((poolAddr) => isConsolidatedReport || selectedPoolAddrs.includes(poolAddr))) {
      lenderAddrSet.add(lenderAddr)
    }
  })
  const fetchDataAndRefresh = async () => {
    try {
      setFetching(true)

      if (isConsolidatedReport && reportRowGeneratorClassForAllPools) {
        reportRowGeneratorClassForAllPools.dateRange = dateRange
        reportRowGeneratorClassForAllPools.endDateTime = endDateTime
        reportRowGeneratorClassForAllPools.timeStep = timeStep
        reportRowGeneratorClassForAllPools.lenderAddr = lenderAddr
        await reportRowGeneratorClassForAllPools.refreshData()
      } else if (reportRowGeneratorClasses) {
        for (const reportRowGeneratorClass of reportRowGeneratorClasses) {
          reportRowGeneratorClass.dateRange = dateRange
          reportRowGeneratorClass.lenderAddr = lenderAddr
          reportRowGeneratorClass.endDateTime = endDateTime
          reportRowGeneratorClass.timeStep = timeStep

          await reportRowGeneratorClass.refreshData()
        }
      }

      setFetching(false)
    } catch (error) {
      setFetching(false)
      logOT.error('Error refreshing data', error)
    }
  }
  const lenderAddrs = Array.from(lenderAddrSet)
  const fetchDisabled =
    fetching ||
    reportRowGeneratorClasses.some((reportRowGeneratorClass) => reportRowGeneratorClass.loading) ||
    (!isConsolidatedReport && selectedPoolAddrs.length === 0) ||
    (isConsolidatedReport && lenderAddr === ZeroAddress && reportDescriptor.key !== REPORT_TICKET_PENDING.key) ||
    reportDescriptor.key == REPORT_DESCRIPTOR_UNSELECTED.key

  const fetchReportDefinition = async (_reportDescriptor: ReportDescriptor): Promise<ReportDefinitionCTO> => {
    otAssert(accountAddr, 'Account address is required')

    const _reportDefinitions: ReportDefinitionCTO[] = await reportDefinitionsForShape(
      serverApiBaseUrl,
      restAPIKey,
      accountAddr,
      _reportDescriptor ?? REPORT_FETCH_DESCRIPTOR_POOL_EVENTS
    )

    let fetchedReportDefinition = _reportDefinitions.find(
      (_reportDefinition) => _reportDefinition.reportDescriptorKey === _reportDescriptor.key
    )
    if (!fetchedReportDefinition) {
      fetchedReportDefinition = {
        reportName: _reportDescriptor.label,
        reportDescriptorKey: _reportDescriptor.key,
        reportUuid: ethers.uuidV4(ethers.randomBytes(16)),
        timestamp: Date.now(),
        dataShape: _reportDescriptor.dataShape,
        accountAddr: accountAddr,
      }
    }

    return fetchedReportDefinition
  }
  const resetToDefaultsReportConfiguration = () => {
    const resetToDefaultsReportConfigurationAsyncFunction = async () => {
      const reportingClass = reportRowGeneratorClasses.find(
        (reportRowGeneratorClass) => reportRowGeneratorClass.poolAddr === selectedTab
      )
      if (!reportingClass || !accountAddr) {
        logOT.error('Error saving report', 'Reporting class not found')
        return
      }

      const newReportDefinition = {
        reportName: reportDescriptor.label,
        reportUuid: ethers.uuidV4(ethers.randomBytes(16)),
        reportDescriptorKey: reportDescriptor.key,
        timestamp: Date.now(),
        dataShape: reportingClass.reportDescriptor?.dataShape ?? ReportDataShape.UNKNOWN,
        accountAddr: accountAddr,
      }

      await reportDefinitionPut(serverApiBaseUrl, restAPIKey, newReportDefinition)

      setKeyIndex(keyIndex + 1)

      setReportDefinition(newReportDefinition)
    }
    resetToDefaultsReportConfigurationAsyncFunction().catch((e) => {
      logOT.error('Error saving report', e)
    })
  }

  const onReportDefinitionChange = (reportDefinition: ReportDefinitionCTO) => {
    setReportDefinition(reportDefinition)
  }

  const saveReportConfiguration = () => {
    const saveReportAsyncFunction = async () => {
      await reportDefinitionPut(serverApiBaseUrl, restAPIKey, reportDefinition)
    }
    saveReportAsyncFunction().catch((e) => {
      logOT.error('Error saving report', e)
    })
  }

  const handleReportDescriptorChange = (event: SelectChangeEvent) => {
    const _reportDescriptorKey = event.target.value as string

    localStorage.setItem('reportDescriptorKey', _reportDescriptorKey)
    const _reportDescriptor = ReportDescriptors.find(
      (reportDescriptor) => reportDescriptor.key === _reportDescriptorKey
    )
    setKeyIndex(keyIndex + 1)
    if (_reportDescriptor) {
      setReportDescriptor(_reportDescriptor)
    }
  }

  const usingCustomColumnSelection = false

  return (
    <Box sx={{ margin: 4, flexDirection: 'column', display: 'flex', gap: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <FormControl sx={{ m: 1, width: 350, padding: '15px' }} size="small">
          <InputLabel id="reportShape-label">Report Type</InputLabel>
          <Select
            size="small"
            style={{ width: 350 }}
            labelId="reportShape-label"
            id="reportShape-select"
            input={<OutlinedInput id="reportShape-select" label="Report Shape" size="small" />}
            value={reportDescriptor.key}
            renderValue={(key: string) => {
              const _reportDescriptor = ReportDescriptors.find((reportDescriptor) => reportDescriptor.key === key)
              if (_reportDescriptor) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '32px', pl: 2 }}>
                    <Typography variant="bodyBold">{_reportDescriptor.label}</Typography>
                  </Box>
                )
              }
              return null
            }}
            onChange={handleReportDescriptorChange}
          >
            {ReportDescriptors.map((reportDescriptor) => {
              return (
                <MenuItem
                  value={reportDescriptor.key}
                  key={reportDescriptor.key}
                  style={{
                    paddingLeft: 12,
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <ListItemText primary={reportDescriptor.label} />
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
        {reportDescriptor.key === 'POOL_EVENTS' && roleEnum === RoleEnum.POOL_ADMIN && (
          <Box sx={{ display: 'flex', flexDirection: 'column', marginLeft: 10 }}>
            <Typography variant="bodyBold">Consolidated Report</Typography>
            <Switch checked={isConsolidatedReport} onChange={() => setIsConsolidatedReport(!isConsolidatedReport)} />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Typography variant="bodyBold">Select vault(s) and date range for the Report</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        {!isConsolidatedReport && reportDescriptor.selectors[ReportSelectorType.POOL_ADDR] && poolAddrs.length > 0 && (
          <ReportVaultMultiSelect vaultCTOs={vaultCTOs} multiple={true} onSelectedChange={onSelectedPoolAddrsChange} />
        )}

        {roleEnum !== RoleEnum.LENDER && reportDescriptor.selectors[ReportSelectorType.LENDER_ADDR] && (
          <ReportLenderSelector
            lenderAddrs={lenderAddrs}
            onSelectedChange={(_lenderAddr) => {
              setLenderAddr(_lenderAddr)
              reportRowGeneratorClasses.forEach((reportRowGeneratorClass) => {
                reportRowGeneratorClass.lenderAddr = _lenderAddr
              })
            }}
          />
        )}

        {reportDescriptor.selectors[ReportSelectorType.DATE_RANGE] && (
          <ReportDateRangeSelector
            selectedPoolAddrs={selectedPoolAddrs}
            isConsolidatedReport={isConsolidatedReport}
            onSelectedChange={(_dayRange: [Dayjs, Dayjs]) => {
              setDateRange(_dayRange)
            }}
            forEvents={true}
          />
        )}
        {reportDescriptor.selectors[ReportSelectorType.END_DATE_TIME] && (
          <ReportDateTimePicker
            onSelectedChange={(_dayTime: Dayjs) => {
              setEndDateTime(_dayTime)
            }}
            forEvents={true}
          />
        )}
        {reportDescriptor.selectors[ReportSelectorType.TIME_STEP] && (
          <ReportDateStepPicker
            onSelectedChange={(_timeStep: TimeStep) => {
              setTimeStep(_timeStep)
            }}
            forEvents={true}
          />
        )}

        <Button
          sx={{
            marginLeft: 6,
            marginTop: 6,
            backgroundColor: theme.palette.fg.main,
            color: theme.palette.fg.fg,
            padding: '8px 12px 8px 12px',
            borderRadius: '6px',
            height: 48,
          }}
          disabled={fetchDisabled}
          variant={'contained'}
          onClick={() => {
            fetchDataAndRefresh()
              .then(() => {
                forceRerender({})
              })
              .catch((error) => {
                logOT.error('Error refreshing data', error)
              })
          }}
        >
          <Typography variant={'buttonBold'}>Generate Report</Typography>
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="bodyBold">Save Report Template</Typography>
        <Typography variant="caption">
          Create a Template: Choose the columns you want included, then click &quot;Save Column Selection&quot;
        </Typography>
        <Typography variant="caption">
          Choose the vaults and date range for your report, then click &quot;Generate Report.&quot;
        </Typography>
        <Typography variant="body" sx={{ marginTop: 2 }}>
          {' '}
          Export Your Report:
        </Typography>
        <Typography variant="caption">Once the data loads, you can export it for your own records.</Typography>
        <Typography variant="body" sx={{ marginTop: 2 }}>
          Refresh or Update Data:
        </Typography>
        <Typography variant="caption">
          To update a report, simply select a different set of vaults or a new date range, then click &quot;Generate
          Report&quot; again.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'right' }}>
        <Button
          sx={{
            marginLeft: 6,
            marginTop: 6,
            backgroundColor: theme.palette.fg.main,
            color: theme.palette.fg.fg,
            padding: '8px 12px 8px 12px',
            borderRadius: '6px',
            height: 48,
          }}
          variant="contained"
          onClick={() => {
            saveReportConfiguration()
          }}
        >
          <Typography variant={'buttonBold'}>Save Column Selection</Typography>
        </Button>
        <Button
          disabled={usingCustomColumnSelection}
          sx={{
            marginLeft: 6,
            marginTop: 6,
            backgroundColor: theme.palette.fg.main,
            color: theme.palette.fg.fg,
            padding: '8px 12px 8px 12px',
            borderRadius: '6px',
            height: 48,
          }}
          variant="contained"
          onClick={() => {
            resetToDefaultsReportConfiguration()
          }}
        >
          <Typography variant={'buttonBold'}>Reset To Defaults</Typography>
        </Button>
      </Box>
      {isConsolidatedReport && reportRowGeneratorClassForAllPools && (
        <ReportExplorer
          reportRowGeneratorClass={reportRowGeneratorClassForAllPools}
          reportDefinition={reportDefinition}
          onReportDefinitionChange={onReportDefinitionChange}
        />
      )}

      {!isConsolidatedReport && (
        <TabContext value={selectedTab}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList
              onChange={(_event: React.SyntheticEvent, newValue: EthAddr) => {
                setSelectedTab(newValue)
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              {selectedPoolAddrs.map((poolAddr) => {
                return (
                  <Tab
                    label={vaultCTOs[poolAddr]?.displayName}
                    value={poolAddr}
                    key={poolAddr + reportDescriptor.key}
                  />
                )
              })}
            </TabList>
          </Box>
          {reportRowGeneratorClasses.map((reportRowGeneratorClass) => {
            return (
              <TabPanel
                value={reportRowGeneratorClass.poolAddr}
                key={reportRowGeneratorClass.poolAddr + reportDescriptor.key + keyIndex}
              >
                <ReportExplorer
                  reportRowGeneratorClass={reportRowGeneratorClass}
                  reportDefinition={reportDefinition}
                  onReportDefinitionChange={onReportDefinitionChange}
                />
              </TabPanel>
            )
          })}
        </TabContext>
      )}
    </Box>
  )
}
